<template>
  <section class="mx-auto max-w-[980px] px-5 py-7 sm:px-8 lg:px-10 lg:py-10">
    <router-link to="/doctor/history" class="inline-flex items-center gap-2 text-sm font-medium text-[#667383] transition hover:text-[#0066ff]">← {{ t('Вернуться к истории') }}</router-link>

    <div v-if="loading" class="mt-6 h-[420px] animate-pulse rounded-2xl border border-[#e4e9ed] bg-white"></div>
    <div v-else-if="error" class="mt-6 rounded-2xl border border-red-100 bg-red-50 p-8 text-center text-sm text-red-700">{{ error }}</div>
    <template v-else-if="response">
      <header class="mt-6 overflow-hidden rounded-2xl border border-[#e1e7eb] bg-white shadow-sm">
        <div class="border-b border-[#edf0f3] px-6 py-6 sm:px-8">
          <div class="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
            <div><p class="text-xs font-semibold uppercase tracking-[0.14em] text-[#0066ff]">{{ t('Результат прохождения') }}</p><h1 class="mt-2 text-2xl font-semibold text-[#202b38]">{{ response.survey_name }}</h1><p class="mt-2 text-sm text-[#7b8795]">{{ t('Завершён') }} · {{ formatDateTime(response.completed_at) }}</p></div>
            <div v-if="response.total_score !== null" class="flex min-w-[120px] flex-col items-center rounded-2xl bg-[#edf5ff] px-5 py-4 text-[#345f98]"><span class="text-3xl font-semibold">{{ response.total_score }}</span><span class="mt-0.5 text-xs font-medium">{{ t('баллов') }}</span></div>
          </div>
        </div>
        <div class="grid gap-5 px-6 py-5 sm:grid-cols-2 sm:px-8">
          <div><p class="text-xs font-medium uppercase tracking-wide text-[#98a2ae]">{{ t('Отметка') }}</p><p class="mt-1.5 text-sm leading-6 text-[#3f4b58]">{{ response.note || t('Без отметки') }}</p></div>
          <div><p class="text-xs font-medium uppercase tracking-wide text-[#98a2ae]">{{ t('Статус') }}</p><span class="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#e9f7ef] px-2.5 py-1 text-xs font-semibold text-[#177448]"><span class="h-1.5 w-1.5 rounded-full bg-[#24915d]"></span> {{ t('Завершён') }}</span></div>
        </div>
      </header>

      <section v-if="assessmentItems.length" class="mt-6 rounded-2xl border border-[#e1e7eb] bg-white p-6 shadow-sm sm:p-8">
        <h2 class="text-lg font-semibold text-[#202b38]">{{ t('Итоговая оценка') }}</h2>
        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <div v-for="item in assessmentItems" :key="item.key" class="rounded-xl border border-[#e6eaee] bg-[#fafbfc] p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-[#35414e]">{{ item.label }}</p>
                <p class="mt-1 text-xs text-[#8a95a2]">{{ levelLabel(item.level) }}</p>
              </div>
              <span class="rounded-lg px-2.5 py-1 text-sm font-semibold" :class="levelTone(item.level)">{{ item.score ?? '—' }}</span>
            </div>
            <div v-if="item.official_interpretation" class="mt-3 border-t border-[#edf0f3] pt-2.5">
              <p class="text-xs font-semibold text-[#5a6876]">{{ t('Официальная интерпретация:') }}</p>
              <p class="mt-1 text-xs leading-relaxed text-[#35414e]">{{ item.official_interpretation }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="mt-6 rounded-2xl border border-[#e1e7eb] bg-white p-6 shadow-sm sm:p-8">
        <div class="flex items-center justify-between"><h2 class="text-lg font-semibold text-[#202b38]">{{ t('Ответы') }}</h2><span class="text-sm text-[#8a95a2]">{{ response.answers.length }} {{ t('вопросов') }}</span></div>
        <div class="mt-5 divide-y divide-[#edf0f3]">
          <div v-for="(answer, index) in response.answers" :key="answer.id" class="grid gap-3 py-5 first:pt-0 last:pb-0 sm:grid-cols-[32px_1fr]">
            <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-[#f0f3f5] text-xs font-semibold text-[#75818e]">{{ index + 1 }}</span>
            <div><p class="text-sm font-medium leading-5 text-[#35414e]">{{ answer.question_text }}</p><p class="mt-2 text-sm leading-6 text-[#667383]">{{ answerValue(answer) }}</p></div>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import api from '@/services/api'
import { t, currentLang } from '@/i18n'

export default {
  name: 'DoctorSurveyResult',
  data() { return { response: null, assessment: null, loading: false, error: '' } },
  computed: {
    assessmentItems() {
      const scales = this.assessment?.summary || this.assessment?.scales || {}
      const labels = {
        phq9: `${this.t('Депрессия')} (PHQ-9)`,
        gad7: `${this.t('Тревожность')} (GAD-7)`,
        depression: this.t('Депрессивные проявления'),
        anxiety: this.t('Симптомы тревоги'),
        stress: this.t('Стрессовое состояние'),
        sdq_total: this.t('Общий показатель SDQ'),
        dshi: this.t('Самоповреждение'),
        suicide: this.t('Суицидальный риск')
      }
      return Object.entries(scales)
        .filter(([, value]) => value && typeof value === 'object' && value.score !== undefined)
        .map(([key, value]) => ({
          key,
          label: labels[key] || key.toUpperCase(),
          score: value.score,
          level: value.level,
          official_interpretation: value.official_interpretation || value.interpretation || ''
        }))
    }
  },
  created() { this.loadResult() },
  methods: {
    t,
    async loadResult() {
      this.loading = true; this.error = ''
      try {
        const [response, assessment] = await Promise.all([
          api.getSurveyResponse(this.$route.params.id),
          api.getRiskAssessment(this.$route.params.id, currentLang.value)
        ])
        this.response = response.data
        this.assessment = assessment.data
      }
      catch (error) { this.error = error.response?.status === 404 ? this.t('Результат не найден') : this.t('Не удалось загрузить результат') }
      finally { this.loading = false }
    },
    answerValue(answer) {
      if (answer.text_value) return answer.text_value
      if (answer.numeric_value !== null && answer.numeric_value !== undefined) return String(answer.numeric_value)
      if (answer.selected_options?.length) return answer.selected_options.map(item => item.text).join(', ')
      if (answer.matrix_values) return Object.entries(answer.matrix_values).map(([key, value]) => `${this.matrixRow(answer, key)}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' · ')
      return this.t('Нет ответа')
    },
    matrixRow(answer, id) { return answer.matrix_rows?.find(row => String(row.id) === String(id))?.text || id },
    levelLabel(level) { const keys = { normal: 'Норма', minimal: 'Минимальный', mild: 'Лёгкий', moderate: 'Умеренный', severe: 'Тяжёлый', extremely_severe: 'Крайне тяжёлый', critical: 'Критический' }; return this.t(keys[level] || 'Результат рассчитан') },
    levelTone(level) { return ['severe', 'extremely_severe', 'critical'].includes(level) ? 'bg-red-50 text-red-700' : level === 'moderate' ? 'bg-amber-50 text-amber-700' : 'bg-[#e9f7ef] text-[#177448]' },
    formatDateTime(value) { return value ? new Intl.DateTimeFormat(currentLang.value === 'kz' ? 'kk-KZ' : 'ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '—' }
  }
}
</script>
